// SupplierPage.js

import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import PageHeader from "common/PageHeader";
import Footer from "common/Footer";
import SupplierList from "common/Suppliers/SupplierList";
import ImportSuppliersButton from "common/Suppliers/ImportSuppliersButton";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MDButton from "components/MDButton";
import { useLanguage } from "context/LanguageContext";

const SupplierPage = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const missingSuppliers = location.state?.missingSuppliers || [];
  const [focusSupplierRegistration, setFocusSupplierRegistration] = useState(
    missingSuppliers.find((item) => item.supplier_registration)?.supplier_registration || ""
  );
  const handleFocusHandled = useCallback(() => setFocusSupplierRegistration(""), []);
  const { t } = useLanguage();

  const handleImportSuccess = (result) => {
    setRefreshTrigger((prev) => prev + 1);
    const firstWithCode = missingSuppliers.find((item) => item.supplier_registration);
    if (firstWithCode) setFocusSupplierRegistration(firstWithCode.supplier_registration);
    setImportResult(result || null);
    setImportDialogOpen(true);
  };

  const renderSupplierList = (title, items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
      <Box mt={2}>
        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
          {title} ({items.length})
        </MDTypography>
        <List
          dense
          sx={{
            py: 0,
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          {items.map((item, idx) => (
            <ListItem key={`${title}-${item?.supplier_registration || item?.name || idx}`} divider>
              <ListItemText
                primary={item?.name || "Unknown"}
                secondary={item?.supplier_registration || item?.reason || "-"}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleRefreshComplete = (succeeded) => {
    if (isRefreshing && succeeded) showSnackbar(t("supplierListRefreshed"), "success");
    setIsRefreshing(false);
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <PageHeader
                title={t("supplierManagement")}
                subtitle={location.state?.fromExport ? t("supplierExportReturnHint") : null}
                actions={
                  <>
                    {location.state?.fromExport && (
                      <MDButton
                        variant="outlined"
                        color="info"
                        onClick={() => navigate("/documents")}
                      >
                        {t("backToDocuments")}
                      </MDButton>
                    )}
                    <Tooltip title={t("refreshList")}>
                      <IconButton
                        color="info"
                        onClick={handleRefresh}
                        sx={{
                          transform: isRefreshing ? "rotate(180deg)" : "none",
                          transition: "transform 0.5s ease-in-out",
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <ImportSuppliersButton onImportSuccess={handleImportSuccess} />
                  </>
                }
              />

              <Box>
                {missingSuppliers.length > 0 && (
                  <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                    <MDTypography variant="body2" mb={1}>
                      {t("suppliersNeedSettings")}
                    </MDTypography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {missingSuppliers.map((supplier, index) => (
                        <MDButton
                          key={`${
                            supplier.supplier_registration || supplier.supplier_name
                          }-${index}`}
                          variant="outlined"
                          color="warning"
                          size="small"
                          disabled={!supplier.supplier_registration}
                          onClick={() =>
                            setFocusSupplierRegistration(supplier.supplier_registration)
                          }
                        >
                          {supplier.supplier_name || supplier.supplier_registration}
                          {!supplier.supplier_registration ? ` (${t("missingRegistration")})` : ""}
                        </MDButton>
                      ))}
                    </Box>
                  </Alert>
                )}
                <SupplierList
                  refreshTrigger={refreshTrigger}
                  onRefreshComplete={handleRefreshComplete}
                  focusSupplierRegistration={focusSupplierRegistration}
                  onFocusHandled={handleFocusHandled}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("importSuppliers")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={1}>
            {t("importCompleted") || "Import completed."}
          </MDTypography>

          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
            <Alert severity="success">
              {t("createdCount").replace("{count}", String(importResult?.summary?.created || 0))}
            </Alert>
            <Alert severity="info">
              {t("updatedCount").replace("{count}", String(importResult?.summary?.updated || 0))}
            </Alert>
            <Alert severity={importResult?.summary?.failed ? "warning" : "info"}>
              {t("failedCount").replace("{count}", String(importResult?.summary?.failed || 0))}
            </Alert>
          </Box>
          {renderSupplierList(t("createdSuppliers"), importResult?.created_suppliers)}
          {renderSupplierList(t("updatedSuppliers"), importResult?.updated_suppliers)}
          {renderSupplierList(t("failedSuppliers"), importResult?.failed_suppliers)}
          {(!Array.isArray(importResult?.created_suppliers) ||
            importResult.created_suppliers.length === 0) && (
            <MDTypography variant="caption" color="text" display="block" mt={2}>
              {t("noCreatedSuppliers")}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={() => setImportDialogOpen(false)}>
            {t("close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default SupplierPage;
